import * as Sentry from "@sentry/angular";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, merge, mergeMap, of, pipe, switchMap, take, tap } from "rxjs";
import { translate } from '@jsverse/transloco';

import { AppNotificationService } from "src/app/shared/services/notification.service";
import * as EmployeeActions from "./employee-actions";
import { EmployeeService } from "src/app/company/services/employee.service";
import { IEmployee } from "src/app/company/models/employee";

@Injectable()
export class EmployeesEffects {

    ////////////////////
    // GET EMPLOYEES //
    //////////////////

    // Get Employees 
    getEmployees$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmployeeActions.getEmployees), 
            mergeMap(() => {
            return this.employeeService.getEmployees({pageNumber: 0, pageSize: 25}).pipe(
                map((response) => 
                    EmployeeActions.getEmployeesSuccess({ employees: response.data })
                ), 
                catchError((error) =>
                    of(EmployeeActions.getEmployeesFailure({ error: error.message }))
                )
            );
        })
        )
    );

    // Get HTECEmployees 
    getHtecEmployees$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmployeeActions.getHtecEmployees), 
            mergeMap(() => {
            return this.employeeService.getHtecEmployees().pipe(
                map((response) => 
                    EmployeeActions.getHtecEmployeesSuccess({ htecEmployees: response.data.map((employee) => { 
                        employee.fullName = `${employee.firstName} ${employee.lastName}`;
                        return employee;
                    })
                })
                ), 
                catchError((error) =>
                    of(EmployeeActions.getCurrentEmployeeFailure({ error: error.message }))
                )
            );
        }),
        take(1)
        )
    );




    ///////////////////////////
    // GET CURERNT EMPLOYEE //
    /////////////////////////

    // Get Current Employee 
    getCurrentEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmployeeActions.getCurrentEmployee), 
            mergeMap(() => {
            return this.employeeService.getCurrentEmployee().pipe(
                map((response) => 
                    EmployeeActions.getCurrentEmployeeSuccess({ employee: response })
                ),
                tap(response => Sentry.setUser({ email: `${response?.employee.email}` })), // set current users email for sentry debugging
                catchError((error) =>
                    of(EmployeeActions.getCurrentEmployeeFailure({ error: error.message }))
                )
            );
        }),
        take(1)
        )
    );




    ///////////////////////
    // CREATE EMPLOYEE  //
    /////////////////////

    // Create Employee 
    createEmployees$ = createEffect(() =>
      this.actions$.pipe(
          ofType(EmployeeActions.createEmployee),
          mergeMap((action) => 
              this.employeeService.createEmployee(action.employee).pipe(
                  map((employee: IEmployee) => EmployeeActions.createEmployeeSuccess({ employee })),
                  catchError((error) => of(EmployeeActions.createEmployeeFailure({ error: error.message })))
              )
          )
      )
    );

    // Employee created successfully
    createEmployeesSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(EmployeeActions.createEmployeeSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('employees.createSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Employee not created successfully
    createEmployeesFailure$ = createEffect(() => 
      this.actions$.pipe(
          ofType(EmployeeActions.createEmployeeFailure),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('employees.createFailed'), style: 'error' });
          })
      ), { dispatch: false }
    );




    ///////////////////////
    // UPDATE EMPLOYEE  //
    /////////////////////

    // Update Employee 
    updateEmployees$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmployeeActions.updateEmployee),
            mergeMap((action) => 
                this.employeeService.updateEmployee(action.employee).pipe(
                    map((employee: IEmployee) => EmployeeActions.updateEmployeeSuccess({ employee })),
                    catchError((error) => of(EmployeeActions.updateEmployeeFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Update Employee successful
    updateEmployeesSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(EmployeeActions.updateEmployeeSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('employees.updateSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Employee not updated successfully
    updateEmployeesFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(EmployeeActions.updateEmployeeFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('employees.updateFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );




    ///////////////////////
    // DELETE EMPLOYEE  //
    /////////////////////

    // Delete Employee 
    deleteEmployees$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmployeeActions.deleteEmployee), 
            mergeMap((action) => {
                return this.employeeService.deleteEmployee(action.id).pipe(
                    map(() => EmployeeActions.deleteEmployeeSuccess({id: action.id})), 
                    catchError((error) => of(EmployeeActions.deleteEmployeeFailure({ error: error.message })))
            )}
        ))
    );

    // Employee deleted successfully
    deleteEmployeesSuccess$ = createEffect(() => 
      this.actions$.pipe(
          ofType(EmployeeActions.deleteEmployeeSuccess),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('employees.deleteSuccess'), style: 'success' });
          })
      ), { dispatch: false }
    );

    // Employee not deleted successfully
    deleteEmployeesFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(EmployeeActions.deleteEmployeeFailure),
        tap(() => {
            this.appnotificationService.notification$.next({ message:translate('employees.deleteFailed'), style: 'error' });
          })
      ), { dispatch: false }
    );




    constructor(
        private actions$: Actions, 
        private employeeService: EmployeeService,
        private appnotificationService: AppNotificationService) {}
}